import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Envelope, Lock, UserPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('job_seeker');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await register(name, email, password, role);
    
    if (result.success) {
      toast.success(role === 'company' ? 'Registration successful! Your company listing will be reviewed by admin.' : 'Registration successful!');
      navigate('/');
    } else {
      toast.error(result.error || 'Registration failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background_light flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white border border-borders rounded-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <UserPlus size={32} className="text-primary" />
            <h1 className="text-3xl tracking-tight font-medium">Register</h1>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Full Name</label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Email</label>
              <div className="relative">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  data-testid="register-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Password</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  data-testid="register-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">I am a...</label>
              <select
                data-testid="register-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="job_seeker">Job Seeker / Technician</option>
                <option value="employer">Employer / Hiring Manager</option>
                <option value="company">Pest Control Company</option>
              </select>
            </div>
            
            {role === 'company' && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm text-sm">
                <p className="text-yellow-800">Company registrations require admin approval before your listing goes live.</p>
              </div>
            )}
            
            <button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="w-full bg-primary text-text_light py-3 rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
          
          <div className="mt-6 text-center text-base leading-relaxed">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-accent hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}